import { useLocation } from '@reach/router'
import React, { FunctionComponent, useEffect, useState } from 'react'

import Loader from '../Loader/Loader'
import EasySoftwarePage from '../../templates/easySoftwarePage'
import PuxHomePage from '../../templates/HomePage/PuxHomePage'
import { getLocalizedData } from '../../utils/localeURL'
import { getBuilderService, initBuilderService } from 'Root/Shared/utils/data/puxBuilderService'
import type { CreatePagesArgs, Reporter } from "gatsby"
import { getLocaleFromUrl, getPrefixedUrl } from '../../utils/edit/editURL'
import { fakeGraphql, fakeReporter, getAuthorizationToken, getEditData, getFakePageProps, getTemplateDataFromUrl } from '../../utils/edit/getEditData'
import ArticlePage from 'Root/Shared/templates/Article/ArticlePage'
import CaseStudyDetail from 'Root/Shared/templates/CaseStudy/CaseStudyDetail'
import FunctionDetail from 'Root/Shared/templates/Function/FunctionDetail'
import NewsDetail from 'Root/Shared/templates/News/NewsDetail'
import CategoryPage from 'Root/Shared/templates/Category/CategoryPage'
import LandingPage from 'Root/Shared/templates/Landing/LandingPage'
import PricingPage from 'Root/Shared/templates/Pricing/PricingPage'
import KnowledgeBaseArticle from 'Root/Shared/templates/KnowledgeBase/KnowledgeBaseArticle'
import FaqDetail from 'Root/Shared/templates/Faq/FaqDetail'
import PuxEditorBar from '../PuxEditor/PuxEditorBar'
import { PuxEditorProvider } from '../PuxEditor/PuxEditorContext'
import NewsAuthor from 'Root/Shared/templates/News/NewsAuthor'
import NewsCategory from 'Root/Shared/templates/News/NewsCategory'
import NewsTag from 'Root/Shared/templates/News/NewsTag'
import KnowledgeBase from 'Root/Shared/templates/KnowledgeBase/KnowledgeBase'
import KnowledgeBaseCategory from 'Root/Shared/templates/KnowledgeBase/KnowledgeBaseCategory'

type PreviewMode = 'preview' | 'edit'

interface PreviewProps {
    mode: PreviewMode
}

const Preview: FunctionComponent<PreviewProps> = ({ mode }) => {
    const location = useLocation();

    if (!process.env.PROJECT || process.env.PROJECT === "EasyRedmineGatsbyAI") {
        return null
    }

    let localePrefix;

    if (process.env.PROJECT === "EasyProjectGatsby") {
        if (process.env.LOCALE && ["cs", "en", "hu"].includes(process.env.LOCALE)) {
            localePrefix = process.env.LOCALE
        }
    } else {
        if (process.env.LOCALE && ["en"].includes(process.env.LOCALE)) {
            localePrefix = process.env.LOCALE
        }
    }

    const localizedConfigData = getLocalizedData(
        getLocaleFromUrl(location.pathname, mode, localePrefix),
        process.env.PROJECT as string
    ) ?? getLocalizedData(process.env.LOCALE!, process.env.PROJECT!)

    const fakeBuilderArgs: Partial<CreatePagesArgs> = {
        graphql: fakeGraphql,
        reporter: new fakeReporter() as Reporter
    }

    initBuilderService(fakeBuilderArgs as CreatePagesArgs, localizedConfigData, false)

    const [state, setState] = useState<any>(undefined)
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(getBuilderService()?.authorization.access_token ? true : false)

    useEffect(() => {
        const fetchToken = async () => await getAuthorizationToken()

        fetchToken().then(() => {
            if (getBuilderService().authorization.access_token) {
                setIsAuthenticated(true)
            } else {
                setIsAuthenticated(false)
            }
        })
    }, [])

    useEffect(() => {
        const cmsUrl = getPrefixedUrl(location.pathname, mode, localePrefix)

        const fetchData = async (templateData, cmsUrl) => {
            return await getEditData({
                contentItemId: templateData.contentItemId ?? ``,
                contentItemVersionId: templateData.contentItemVersionId ?? ``,
                contentType: templateData.contentType ?? ``,
                path: cmsUrl ?? ``,
            })
        }

        if (isAuthenticated) {
            getTemplateDataFromUrl(cmsUrl).then((templateData) => {
                if (!templateData) {
                    setState(undefined)
                    return
                }

                fetchData(templateData, cmsUrl).then(async (pageData) => {
                    setState({
                        context: pageData?.context, pageType: templateData.contentType ?? ""
                    })
                })
            })
        } else {
            setState({ context: undefined, pageType: undefined })
        }
    }, [isAuthenticated, mode])

    function renderLogin() {
        return (
            <>
                <PuxEditorProvider><PuxEditorBar isAuthenticated={false} isPreview={mode === 'preview'} /></PuxEditorProvider>
                <div className='u-marginTop120 u-textCenter'>
                    <h1>Log in to your orchard account.</h1>
                    <p className='u-marginTop80'>Are you logged in? Please <a href="javascript:void(0)" onClick={() => window.location.reload()}>refresh this page 🔃</a>.</p>
                </div>
            </>
        )
    }

    function renderMissingTemplate() {
        const featureName = mode === 'preview' ? 'preview' : 'editor'
        return (
            <>
                <div className='u-marginTop120 u-textCenter'>
                    <h1>Missing Template.</h1>
                    <p className='u-marginTop80'>This page does not have {featureName} feature. <br />Contact Dev Team.</p>
                </div>
            </>
        )
    }

    function renderContent() {
        const pageProps = getFakePageProps(location, state?.context)
        const loadingMessage = mode === 'preview' ? 'Loading Preview Mode' : 'Loading Editor Mode'

        if (state) {
            switch (state.pageType) {
                case `EasySoftwarePage`:
                    return (
                        <EasySoftwarePage
                            {...pageProps}
                        />
                    )
                case `PuxHomePage`:
                    return (
                        <PuxHomePage
                            {...pageProps}
                        />
                    )
                case `EasySoftwareArticle`:
                    return (
                        <ArticlePage
                            {...pageProps}
                        />
                    )
                case `EasySoftwareCaseStudy`:
                    return (
                        <CaseStudyDetail
                            {...pageProps}
                        />
                    )
                case `EasySoftwareFunction`:
                    return (
                        <FunctionDetail
                            {...pageProps}
                        />
                    )
                case `PuxAuthor`:
                    return (
                        <NewsAuthor
                            {...pageProps}
                        />
                    )
                case `EasySoftwareNewsCategory`:
                    return (
                        <NewsCategory
                            {...pageProps}
                        />
                    )
                case `EasySoftwareNewsTag`:
                    return (
                        <NewsTag
                            {...pageProps}
                        />
                    )
                case `EasySoftwareNews`:
                    return (
                        <NewsDetail
                            {...pageProps}
                        />
                    )
                case `EasySoftwareCategoryPage`:
                    return (
                        <CategoryPage
                            {...pageProps}
                        />
                    )
                case `PuxLandingPage`:
                    return (
                        <LandingPage
                            {...pageProps}
                        />
                    )
                case `EasySoftwarePricingPage`:
                    return (
                        <PricingPage
                            {...pageProps}
                        />
                    )
                case `EasySoftwareKnowledgeBase`:
                    return (
                        <KnowledgeBase
                            {...pageProps}
                        />
                    )
                case `EasySoftwareKnowledgeBaseCategory`:
                    return (
                        <KnowledgeBaseCategory
                            {...pageProps}
                        />
                    )
                case `EasySoftwareKnowledgeBaseArticle`:
                    return (
                        <KnowledgeBaseArticle
                            {...pageProps}
                        />
                    )
                case `EasySoftwareFaq`:
                    return (
                        <FaqDetail
                            {...pageProps}
                        />
                    )
                case undefined:
                    return <Loader message={loadingMessage} />
                default:
                    return renderMissingTemplate()
            }
        }

        return <Loader message={loadingMessage} />
    }

    if (!isAuthenticated) {
        return renderLogin()
    }

    return <div>{renderContent()}</div>
}

export default Preview
